import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import { ETIQUETTE_TIPS, ETIQUETTE_CATEGORIES, getTipsByCategory, type EtiquetteTip } from "../../mocks/etiquette";

const LEVEL_LABEL: Record<string, { label: string; color: string }> = {
  basic: { label: "기본", color: "text-emerald-500/80 bg-emerald-500/10 border-emerald-500/20" },
  intermediate: { label: "중급", color: "text-amber-500/80 bg-amber-500/10 border-amber-500/20" },
  advanced: { label: "심화", color: "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" },
};

function TipCard({ tip, onClick }: { tip: EtiquetteTip; onClick: () => void }) {
  const level = LEVEL_LABEL[tip.level];
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-[#111112] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] hover:bg-white/[0.02] transition-all cursor-pointer group"
    >
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0" style={{ background: tip.categoryColor + "15", border: `1px solid ${tip.categoryColor}25` }}>
          <i className={`${tip.categoryIcon} text-xs`} style={{ color: tip.categoryColor }}></i>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${level.color}`}>{level.label}</span>
        <i className="ri-arrow-right-line text-[#333333] text-xs ml-auto group-hover:text-[#888888] transition-colors"></i>
      </div>
      <p className="text-[#CCCCCC] text-xs font-semibold leading-snug mb-1.5 group-hover:text-[#F2F2F2] transition-colors">{tip.title}</p>
      <p className="text-[#555555] text-[11px] leading-relaxed line-clamp-2">{tip.summary}</p>
    </button>
  );
}

function TipDetail({ tip, onClose }: { tip: EtiquetteTip; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-[#111112] border border-white/[0.1] rounded-2xl overflow-hidden shadow-2xl animate-[fadeIn_0.25s_ease] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.07] flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: tip.categoryColor + "18", border: `1px solid ${tip.categoryColor}28` }}>
              <i className={`${tip.categoryIcon} text-base`} style={{ color: tip.categoryColor }}></i>
            </div>
            <div>
              <p className="text-[#888888] text-[10px] mb-0.5">{ETIQUETTE_CATEGORIES.find(c => c.key === tip.category)?.label}</p>
              <h3 className="text-[#F2F2F2] font-semibold text-sm leading-snug">{tip.title}</h3>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[#444444] hover:text-[#888888] cursor-pointer shrink-0 transition-colors mt-0.5">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Summary */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3">
            <p className="text-[#AAAAAA] text-xs leading-relaxed italic">"{tip.summary}"</p>
          </div>

          {/* Detail */}
          <div>
            <p className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">상세 설명</p>
            <p className="text-[#888888] text-[13px] leading-relaxed">{tip.detail}</p>
          </div>

          {/* Do / Don't */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-400/[0.04] border border-emerald-400/15 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <i className="ri-checkbox-circle-line text-emerald-400/80 text-sm"></i>
                <span className="text-emerald-400/80 text-[10px] font-semibold uppercase tracking-wider">이렇게 하세요</span>
              </div>
              <div className="space-y-2">
                {tip.doList.map((item, i) => (
                  <p key={i} className="text-[#888888] text-[11px] leading-relaxed flex items-start gap-1.5">
                    <span className="text-emerald-400/60 mt-0.5 shrink-0">✓</span>
                    {item}
                  </p>
                ))}
              </div>
            </div>
            <div className="bg-red-400/[0.04] border border-red-400/15 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <i className="ri-close-circle-line text-red-400/80 text-sm"></i>
                <span className="text-red-400/80 text-[10px] font-semibold uppercase tracking-wider">이건 피하세요</span>
              </div>
              <div className="space-y-2">
                {tip.dontList.map((item, i) => (
                  <p key={i} className="text-[#888888] text-[11px] leading-relaxed flex items-start gap-1.5">
                    <span className="text-red-400/60 mt-0.5 shrink-0">✗</span>
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#4B6CF7]/[0.06] border border-[#4B6CF7]/15 rounded-xl">
            <i className="ri-graduation-cap-line text-[#7A9BFF] text-sm shrink-0"></i>
            <p className="text-[#7A9BFF]/70 text-[11px]">이 내용을 실제 업무에서 의식적으로 실천해 보세요. 반복할수록 자연스러워집니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EtiquettePage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedTip, setSelectedTip] = useState<EtiquetteTip | null>(null);
  const [search, setSearch] = useState("");

  const filteredTips = ETIQUETTE_TIPS.filter(t => {
    const matchCat = activeCategory === "all" || t.category === activeCategory;
    const matchSearch = search === "" ||
      t.title.includes(search) ||
      t.summary.includes(search) ||
      t.detail.includes(search);
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5 overflow-y-auto">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20">
                <i className="ri-medal-line text-amber-400/80 text-base"></i>
              </div>
              <div>
                <h1 className="text-[#F2F2F2] font-semibold text-lg">비즈니스 예절 가이드</h1>
                <p className="text-[#555555] text-xs">이메일·회의·보고·소통 등 직장 생활 필수 예절 {ETIQUETTE_TIPS.length}가지</p>
              </div>
            </div>
          </div>

          {/* Category Banner */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {ETIQUETTE_CATEGORIES.slice(0, 4).map(cat => {
              const count = getTipsByCategory(cat.key).length;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                    activeCategory === cat.key
                      ? "border-opacity-40 bg-white/[0.03]"
                      : "border-white/[0.05] bg-[#111112] hover:border-white/[0.1]"
                  }`}
                  style={activeCategory === cat.key ? { borderColor: cat.color + "50" } : {}}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: cat.color + "15", border: `1px solid ${cat.color}25` }}>
                      <i className={`${cat.icon} text-sm`} style={{ color: cat.color }}></i>
                    </div>
                    <span className="text-[10px] text-[#555555]">{count}가지</span>
                  </div>
                  <p className="text-[#CCCCCC] text-xs font-semibold">{cat.label}</p>
                  <p className="text-[#444444] text-[10px] leading-relaxed mt-0.5 line-clamp-2">{cat.description}</p>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-4 gap-3 mb-6">
            {ETIQUETTE_CATEGORIES.slice(4).map(cat => {
              const count = getTipsByCategory(cat.key).length;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                    activeCategory === cat.key
                      ? "border-opacity-40 bg-white/[0.03]"
                      : "border-white/[0.05] bg-[#111112] hover:border-white/[0.1]"
                  }`}
                  style={activeCategory === cat.key ? { borderColor: cat.color + "50" } : {}}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: cat.color + "15", border: `1px solid ${cat.color}25` }}>
                      <i className={`${cat.icon} text-sm`} style={{ color: cat.color }}></i>
                    </div>
                    <span className="text-[10px] text-[#555555]">{count}가지</span>
                  </div>
                  <p className="text-[#CCCCCC] text-xs font-semibold">{cat.label}</p>
                  <p className="text-[#444444] text-[10px] leading-relaxed mt-0.5 line-clamp-2">{cat.description}</p>
                </button>
              );
            })}
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center gap-1 bg-[#111112] border border-white/[0.06] rounded-lg px-3 py-2 flex-1 max-w-xs">
              <i className="ri-search-line text-[#444444] text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="예절 검색..."
                className="bg-transparent text-[#CCCCCC] text-xs outline-none flex-1 placeholder-[#333333]"
              />
            </div>

            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => setActiveCategory("all")}
                className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${activeCategory === "all" ? "bg-white/[0.08] border-white/[0.15] text-[#CCCCCC]" : "border-white/[0.05] text-[#555555] hover:text-[#888888]"}`}
              >
                전체
              </button>
              {ETIQUETTE_CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${activeCategory === cat.key ? "text-white border-opacity-50" : "border-white/[0.05] text-[#555555] hover:text-[#888888]"}`}
                  style={activeCategory === cat.key ? { backgroundColor: cat.color + "20", borderColor: cat.color + "40", color: cat.color } : {}}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <span className="ml-auto text-[#333333] text-xs whitespace-nowrap">{filteredTips.length}가지</span>
          </div>

          {/* Tips Grid */}
          <div className="grid grid-cols-3 gap-3">
            {filteredTips.map(tip => (
              <TipCard key={tip.id} tip={tip} onClick={() => setSelectedTip(tip)} />
            ))}
          </div>

          {filteredTips.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-search-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#333333] text-sm">검색 결과가 없습니다</p>
            </div>
          )}

          {/* Bottom Banner */}
          <div className="mt-8 bg-gradient-to-r from-amber-500/[0.07] to-[#4B6CF7]/[0.07] border border-white/[0.06] rounded-2xl p-6 flex items-center gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/20 shrink-0">
              <i className="ri-lightbulb-flash-line text-amber-400 text-xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-[#F2F2F2] font-semibold text-sm mb-1">비즈니스 예절은 한 번에 익히는 것이 아닙니다</p>
              <p className="text-[#666666] text-xs leading-relaxed">
                이 플랫폼에서 업무를 수행하면서 이메일을 작성하고, 결재를 올리고, 팀원과 협업할 때마다 위의 예절을 의식적으로 실천해 보세요.
                반복이 자연스러운 직장인 태도를 만듭니다.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/inbox")}
              className="px-4 py-2 bg-amber-500/15 text-amber-400/80 border border-amber-500/25 rounded-xl text-xs font-medium hover:bg-amber-500/25 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              이메일 실습하기 →
            </button>
          </div>

        </main>
      </div>

      {selectedTip && <TipDetail tip={selectedTip} onClose={() => setSelectedTip(null)} />}
    </div>
  );
}
